import type { ApiStoreDeliveryOptions } from '../../api/cart/responses/cartResponses';
import type { ApiAddMultipleRecipesRequest } from '../../api/recipe/requests/recipeApiRequests';
import type { HybrisDeliveryOptionsPointOfService } from '../../models/hybris/hybrisDeliveryOptions';

import { appConfig } from '../../../../appConfig';
import { appInsights } from '../../../../appInsights';
import { Cookies } from '../../../../utility/cookies';
import { getAuthToken, getAuthTokenOrDefault } from '../../api/auth/getAuthToken';
import cartApi from '../../api/cart/cartApi';
import directCartApi from '../../api/cart/directCartApi';
import savedCartsApi from '../../api/savedCarts/savedCartsApi';
import { directStoreApi } from '../../api/store/directStoreApi';
import { mapTimeslot } from '../../api/timeslots/mapTimeslot';
import { DeliveryMode } from '../../store/structureDefinitions/storeState';
import { mapPointOfService, mapStore } from '../../utils/storeUtils';
import { productFlow } from '../product/productFlow';
import { mapCart, mapProductToApiFullCartItem } from './mapCart';

const CART_COOKIE_NAME = 'coop-cart';
export const HOME_DELIVERY_MODE = 'homedelivery';
const getCart = async (cartId: string) => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();

    const directCart = await directCartApi.getCart(shoppingUserId, cartId, token);
    return mapCart(directCart);
};

const mergeCart = async (fromCartId: string, toCartId: string) => {
    const { token, shoppingUserId } = await getAuthToken();

    const newCart = await directCartApi.mergeCarts(shoppingUserId, fromCartId, toCartId, token);
    return mapCart(newCart);
};

/** Get anonymous cart id from cookie
 * @return cart id, this can also be a previous cartid from logged in user, we fucked this one up!
 */
const getAnonymousCartId = () => {
    const anonymousCartId = Cookies.read(CART_COOKIE_NAME);
    return anonymousCartId;
};

const getAnonymousCart = async (cartId: string) => {
    const directCart = await directCartApi.getCart('anonymous', cartId, undefined);
    return mapCart(directCart);
};

const getCurrentCartId = (
    isAuthenticated: boolean | undefined = appConfig.coopUserSettings.isAuthenticated,
) => {
    if (isAuthenticated) {
        return 'current';
    }
    return getAnonymousCartId();
};

const emptyCart = async (cartId: string) => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    return directCartApi.emptyCart(shoppingUserId, cartId, token);
};

const buyMultipleRecipes = (request: ApiAddMultipleRecipesRequest) =>
    cartApi.recipes.buyMultipleRecipes(request);

const removeRecipe = (recipeId: string) => cartApi.recipes.removeRecipe(recipeId);

const changeRecipePortions = (recipeId: string, portions: number) =>
    cartApi.recipes.changeRecipePortions(recipeId, portions);

const changeItemVariant = (
    cartItemId: string,
    productId: string,
    quantity: number,
    newVariantId: string,
) =>
    cartApi.changeCartItemVariant({
        id: cartItemId,
        productId,
        variantId: newVariantId,
        quantity,
    });

const updateItemQuantity = async (
    cartId: string,
    productId: string,
    variantId: string | undefined,
    quantity: number,
    itemReplaceable?: boolean,
) => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();

    return directCartApi.updateCartItem(token)(shoppingUserId, cartId, {
        productId,
        variantId,
        quantity,
        itemReplaceable,
    });
};

/** Set delivery Mode on cart */
const setDeliveryMode = async (cartId: string, deliveryCode: string) => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    await directCartApi.setDeliveryMode(shoppingUserId, cartId, deliveryCode, token);
};

/** Creates a new cart,
 * if syncWithStoreContext, we will try to update the cart with delivermode / zip / pickup-point
 * */
const createCartAsync = async (
    deliveryMode?: DeliveryMode,
    currentZipCode?: string,
    currentPickupPointId?: string,
) => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    const cart = await directCartApi.createCart(shoppingUserId, token);
    const cartId = cart.guid;
    Cookies.create(CART_COOKIE_NAME, cartId, 70);

    if (deliveryMode === DeliveryMode.homedelivery && currentZipCode) {
        const store = await directStoreApi.getStoreByPostCode(currentZipCode);
        await directCartApi.changePostCode(
            shoppingUserId,
            cartId,
            currentZipCode,
            true,
            store.code,
            token,
        );
        await directCartApi.setDeliveryMode(shoppingUserId, cartId, HOME_DELIVERY_MODE, token);
    }
    if (deliveryMode === DeliveryMode.pickup && currentPickupPointId) {
        const pointOfService = await directStoreApi.getPointOfService(currentPickupPointId);
        if (!pointOfService?.pickupDeliveryModes?.length) {
            throw new Error('No deliverymodes!');
        }
        await directCartApi.changePointOfService(
            shoppingUserId,
            cartId,
            currentPickupPointId,
            true,
            token,
        );
        await directCartApi.setDeliveryMode(
            shoppingUserId,
            cartId,
            pointOfService.pickupDeliveryModes[0].code,
            token,
        );
    }

    if (deliveryMode) {
        return mapCart(await directCartApi.getCart(shoppingUserId, cartId, token));
    }
    return mapCart(cart);
};

const getItemSubstitutes = async (rowNumber: string, storeId: string) => {
    const substituteProducts = await cartApi.recipes.getProductSubstitutes(rowNumber);

    const incompleteProductIds = substituteProducts.map((product) => {
        return product.id;
    });

    const products = await productFlow.getProductsByIds([...incompleteProductIds], storeId);
    return products;
};

const substituteProduct = async (cartItemId: string, newProductId: string, storeId: string) => {
    const subsituteResponse = await cartApi.recipes.substituteProduct(cartItemId, newProductId);

    const fullProduct = await productFlow.getProductById(
        subsituteResponse.productIdentifier,
        storeId,
    );

    const cartItem = fullProduct
        ? mapProductToApiFullCartItem(subsituteResponse, fullProduct)
        : null;

    return cartItem;
};

const applyVoucher = async (id: string) => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    return directCartApi.applyVoucher({ voucherId: id, token, shoppingUserId });
};

const removeVoucher = async (id: string) => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    await directCartApi.removeVoucher({ voucherId: id, token, shoppingUserId });
};

const changeReplaceability = async (
    productId: string,
    variantId: string | undefined,
    quantity: number, // needs to be there since we have one endpoint that does not like empty quantity
    replaceable: boolean,
) => {
    const cartId = getCurrentCartId();
    if (!cartId) {
        const error = Error('No cart id');
        appInsights.trackException({ exception: error }, { place: 'changeReplaceability' });
        throw error;
    }
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    const updatedItem = await directCartApi.updateCartItemOld(token)(shoppingUserId, cartId, {
        productId,
        variantId,
        quantity,
        itemReplaceable: replaceable,
    });
    return updatedItem;
};

const setItemsReplaceability = (replaceable: boolean) =>
    cartApi.setItemsReplaceability({ replaceAll: replaceable });

const addToCartFromOrder = async (orderId: string) => {
    const { token, shoppingUserId } = await getAuthToken();
    const cartId = getCurrentCartId();
    if (!cartId) {
        const error = Error('No cart id');
        appInsights.trackException({ exception: error }, { place: 'addToCartFromOrder' });
        throw error;
    }
    const response = await directCartApi.addToCartFromOrder(token)(shoppingUserId, cartId, orderId);
    return response;
};

const restoreSavedCart = async (savedCartId: string) => {
    const { token, shoppingUserId } = await getAuthToken();
    const cartId = getCurrentCartId();
    if (!cartId) {
        const error = Error('No cart id');
        appInsights.trackException({ exception: error }, { place: 'restoreSavedCart' });
        throw error;
    }
    const response = await savedCartsApi.restoreSavedCart(token)(
        shoppingUserId,
        cartId,
        savedCartId,
    );
    return response;
};

const validateAssortment = async () => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    return cartApi.validateAssortment(token)(shoppingUserId);
};

const addItemsBulk = async (
    cartId: string,
    items: {
        productId: string;
        variantId?: string;
        quantity: number;
        replaceable?: boolean;
    }[],
) => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    const response = await directCartApi.bulkEntries(token)(
        shoppingUserId,
        cartId,
        items
            .map((item) => ({
                productCode: item.productId,
                varianceCode: item.variantId,
                quantity: item.quantity,
                replaceable: item.replaceable ?? true,
            }))
            .filter((item) => item.quantity > 0),
    );
    return response;
};

const getDeliveryOptions = async (postalCode: string) => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    const cartId = getCurrentCartId();

    if (!cartId) {
        const error = Error('No cart id');
        appInsights.trackException({ exception: error }, { place: 'getDeliveryOptions' });
        throw error;
    }

    const hybrisDeliveryOptions = await cartApi.getPostalCodeDeliveryOptions(
        shoppingUserId,
        cartId,
        postalCode,
        token,
    );

    // first pickup store must be fullselection
    const fullselectionPickupStore = hybrisDeliveryOptions.pointsOfService?.find(
        (item) => !item.isFoodBagOnly,
    );
    const otherPickupStores =
        hybrisDeliveryOptions.pointsOfService
            ?.filter((item) => item.name !== fullselectionPickupStore?.name)
            .filter((_item, index) => index <= 13) || [];

    const recommendedPickupStores: HybrisDeliveryOptionsPointOfService[] = [];

    if (fullselectionPickupStore != null) {
        recommendedPickupStores.push(fullselectionPickupStore);
    }

    recommendedPickupStores.push(...otherPickupStores);

    const homeDeliveryTimeslots =
        hybrisDeliveryOptions.homeDeliveryWindows?.listOfTimeWindowListWsDTO
            .flatMap((item) => item.timeWindowListWsDTO)
            .filter((item) => !item.fullyBooked)
            .filter((_item, index) => index <= 14)
            .map(mapTimeslot)
            .filter(Boolean) || [];

    const deliveryOptions: ApiStoreDeliveryOptions = {
        pointsOfService: recommendedPickupStores.map((pos) => ({
            ...mapPointOfService(pos),
            earliestAvailableTimeslotsForEachType: pos.earliestAvailableTimeWindowsForEachType
                .map(mapTimeslot)
                .filter(Boolean),
        })),
        homeDeliveryWindows: homeDeliveryTimeslots,
        pickupInStoreEarliestDate: hybrisDeliveryOptions.pickupInStoreEarliestDate,
        expressStore:
            hybrisDeliveryOptions.expressStore && mapStore(hybrisDeliveryOptions.expressStore),
    };

    return deliveryOptions;
};

const deactivateECoupon = async (isAuthenticated: true, ecouponId: string) => {
    const cartId = getCurrentCartId(isAuthenticated);
    if (!cartId) {
        const error = Error('No cart id');
        appInsights.trackException({ exception: error }, { place: 'deactivateECoupon' });
        throw error;
    }
    const { token, shoppingUserId } = await getAuthToken();
    return directCartApi.deactivateECoupon(shoppingUserId, cartId, ecouponId, token);
};

const activateECoupon = async (isAuthenticated: true, ecouponId: string) => {
    const cartId = getCurrentCartId(isAuthenticated);
    if (!cartId) {
        const error = Error('No cart id');
        appInsights.trackException({ exception: error }, { place: 'activateECoupon' });
        throw error;
    }
    const { token, shoppingUserId } = await getAuthToken();
    return directCartApi.activateECoupon(shoppingUserId, cartId, ecouponId, token);
};

export const currentCartFlow = {
    getCurrentCartId,
    getCart,
    getAnonymousCartId,
    getAnonymousCart,
    mergeCart,
    createCartAsync,
    emptyCart,
    removeRecipe,
    buyMultipleRecipes,
    addItemsBulk,
    changeRecipePortions,
    setDeliveryMode,
    changeItemVariant,
    updateItemQuantity,
    getItemSubstitutes,
    substituteProduct,
    applyVoucher,
    removeVoucher,
    changeReplaceability,
    setItemsReplaceability,
    restoreSavedCart,
    addToCartFromOrder,
    validateAssortment,
    getDeliveryOptions,
    activateECoupon,
    deactivateECoupon,
};
